import { Injectable, inject } from '@angular/core';
import { NetworkConstants, checkExistingRoles } from 'na-lib';
import { Firestore, collection, deleteDoc, doc, getCountFromServer, getDoc, getDocs, limit, orderBy, query, setDoc, startAfter, where, writeBatch, type DocumentData, type QueryDocumentSnapshot } from '@angular/fire/firestore';
import { from, map, tap } from 'rxjs';
import { SettingsService } from './settings.service';

export interface ImportedTridy {
  id: string;
  payload: Record<string, unknown>;
  nfcURLPrefix?: string;
}

export interface TridyPageResult {
  items: any[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  total: number;
  hasMore: boolean;
}

export interface ImportedTridyUpdate {
  id: string;
  payload: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class TridysService {
  db = inject(Firestore)
  allTridys$:any=[]

  constructor(private settingsService: SettingsService) { }

  async createTridy(tridyForm):Promise<string>{
    return new Promise(async (resolve, reject) => {
      const docRef = doc( collection(this.db, NetworkConstants.COLLECTION_TRIDYS) )
      const newId = docRef.id

      let newTridy = tridyForm
      newTridy['creationDate'] = new Date()
      newTridy['timeStamp'] = new Date()
      newTridy['id'] = newId

      try{
        await setDoc( docRef, newTridy)
        resolve(newId)
      }catch (error) {
        reject(error)
      }
    })

  }

  /** Creates imported Tridys in Firestore-safe batches. Preview Tridys are never created here. */
  async createImportedTridys(
    contentTypeId: string,
    payloads: any[],
    sourceFileName: string,
    onProgress?: (completed: number, total: number) => void
  ): Promise<ImportedTridy[]> {
    // Smaller than Firestore's 500-write maximum so imports provide useful,
    // visible progress updates while still committing efficiently.
    const chunkSize = 100;
    let completed = 0;
    const created: ImportedTridy[] = [];
    onProgress?.(completed, payloads.length);
    for (let index = 0; index < payloads.length; index += chunkSize) {
      const batch = writeBatch(this.db);
      const currentBatch = payloads.slice(index, index + chunkSize);
      currentBatch.forEach(payload => {
        const ref = doc(collection(this.db, NetworkConstants.COLLECTION_TRIDYS));
        batch.set(ref, {
          id: ref.id,
          contextId: this.settingsService.contextId$,
          contentType: contentTypeId,
          payload,
          sourceFileName,
          source: { type: 'excel', fileName: sourceFileName },
          creationDate: new Date(),
          timeStamp: new Date(),
          sample: false
        });
        created.push({ id: ref.id, payload });
      });
      await batch.commit();
      completed += currentBatch.length;
      onProgress?.(completed, payloads.length);
    }
    return created;
  }

  /** Finds existing DPPs in bounded Firestore queries for an import preview. */
  async findImportedTridys(
    contentTypeId: string,
    identifierPath: string,
    identifiers: unknown[]
  ): Promise<Map<string, any[]>> {
    const uniqueIdentifiers = [...new Map(identifiers
      .filter(value => value !== undefined && value !== null && String(value).trim() !== '')
      .map(value => [this.identifierKey(value), value]))
      .values()];
    const result = new Map<string, any[]>();
    const ref = collection(this.db, NetworkConstants.COLLECTION_TRIDYS);
    const fieldPath = identifierPath === 'id' ? 'id' : `payload.${identifierPath}`;
    const requestedIdentifierKeys = new Set(uniqueIdentifiers.map(value => this.identifierKey(value)));

    // Older DPPs can have a Firestore document ID without a stored `id` field.
    // The list displays that document ID, so the update lookup must accept it too.
    if (identifierPath === 'id') {
      for (let index = 0; index < uniqueIdentifiers.length; index += 30) {
        const identifiersChunk = uniqueIdentifiers.slice(index, index + 30);
        const snapshots = await Promise.all(identifiersChunk.map(value =>
          getDoc(doc(this.db, NetworkConstants.COLLECTION_TRIDYS, String(value).trim()))
        ));
        snapshots.forEach((snapshotDoc, offset) => {
          if (!snapshotDoc.exists()) return;
          const data = snapshotDoc.data() as any;
          if (data?.contextId !== this.settingsService.contextId$) return;
          const key = this.identifierKey(identifiersChunk[offset]);
          result.set(key, [{ ...data, id: snapshotDoc.id }]);
        });
      }
    }

    for (let index = 0; index < uniqueIdentifiers.length; index += 30) {
      const identifiersChunk = uniqueIdentifiers.slice(index, index + 30)
        .filter(value => !result.has(this.identifierKey(value)));
      if (!identifiersChunk.length) continue;
      const importedQuery = identifierPath === 'id'
        ? query(ref,
            where('contextId', '==', this.settingsService.contextId$),
            where('id', 'in', identifiersChunk))
        : query(ref,
            where('contextId', '==', this.settingsService.contextId$),
            where('contentType', '==', contentTypeId),
            where(fieldPath, 'in', identifiersChunk));
      let snapshot;
      try {
        snapshot = await getDocs(importedQuery);
      } catch (error: any) {
        if (error?.code !== 'failed-precondition') throw error;
        const fallbackQuery = identifierPath === 'id'
          ? query(ref, where('contextId', '==', this.settingsService.contextId$))
          : query(ref,
              where('contextId', '==', this.settingsService.contextId$),
              where('contentType', '==', contentTypeId));
        snapshot = await getDocs(fallbackQuery);
      }
      snapshot.docs.forEach(snapshotDoc => {
        const data = snapshotDoc.data() as any;
        const value = identifierPath === 'id'
          ? (data?.id || snapshotDoc.id)
          : this.valueAtPath(data?.payload, identifierPath);
        if (!requestedIdentifierKeys.has(this.identifierKey(value))) return;
        const key = this.identifierKey(value);
        result.set(key, [...(result.get(key) || []), { ...data, id: snapshotDoc.id }]);
      });
    }
    return result;
  }

  /** Merges imported payload fields without replacing fields not present in the file. */
  async mergeImportedTridys(
    updates: ImportedTridyUpdate[],
    sourceFileName: string,
    onProgress?: (completed: number, total: number) => void
  ): Promise<void> {
    const chunkSize = 100;
    let completed = 0;
    onProgress?.(completed, updates.length);
    for (let index = 0; index < updates.length; index += chunkSize) {
      const batch = writeBatch(this.db);
      const currentBatch = updates.slice(index, index + chunkSize);
      currentBatch.forEach(update => {
        const ref = doc(this.db, NetworkConstants.COLLECTION_TRIDYS, update.id);
        batch.set(ref, {
          payload: update.payload,
          timeStamp: new Date(),
          lastImport: { type: 'excel-update', fileName: sourceFileName, timeStamp: new Date() }
        }, { merge: true });
      });
      await batch.commit();
      completed += currentBatch.length;
      onProgress?.(completed, updates.length);
    }
  }

  async getTodayTridyCreationCount(): Promise<number> {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const ref = collection(this.db, NetworkConstants.COLLECTION_TRIDYS);
    const todayQuery = query(
      ref,
      where('contextId', '==', this.settingsService.contextId$),
      where('creationDate', '>=', startOfToday)
    );
    return (await getCountFromServer(todayQuery)).data().count;
  }

  async deleteTridy(tridyId: string): Promise<void> {
    if (!tridyId) return;
    const ref = doc(this.db, NetworkConstants.COLLECTION_TRIDYS, tridyId);
    await deleteDoc(ref);
  }

  async createTridyFromContentType(data, contentTypeId):Promise<string>{
    return new Promise(async (resolve, reject) => {
  
      let newTridy = data
      newTridy['creationDate'] = new Date()
      newTridy['timeStamp'] = new Date()
      newTridy['contentType'] = contentTypeId
      newTridy['sample']= true

      try{
        const docRef = doc( this.db, NetworkConstants.COLLECTION_TRIDYS, contentTypeId)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          resolve('The tridy with contentTypeId already exists')
        } else {
          await setDoc( docRef, newTridy,{merge:true} )
          resolve(contentTypeId)
        }
      }catch (error) {
        reject(error)
      }
    })

  }

  observeTridyIdDoc(tridyId:string){
    const ref = doc(this.db, `${NetworkConstants.COLLECTION_TRIDYS}/${tridyId}`);
    return from(getDoc(ref));
  }

  async getTridyReferences(tridyId: string): Promise<{ incoming: any[]; outgoing: any[] }> {
    if (!tridyId) return { incoming: [], outgoing: [] };
    const referencesRef = collection(this.db, NetworkConstants.COLLECTION_TRIDYS, tridyId, 'References');
    const snapshot = await getDocs(query(referencesRef, orderBy('creationDate', 'asc')));
    const references = snapshot.docs.map(reference => ({ ...reference.data(), id: reference.id }));
    return {
      incoming: references.filter(reference => reference['type'] === 'incoming'),
      outgoing: references.filter(reference => reference['type'] === 'outgoing')
    };
  }

  async getTridysPage(pageSize = 50, lastDoc?: QueryDocumentSnapshot<DocumentData>): Promise<TridyPageResult> {
    const contextId = this.settingsService.contextId$;
    const ref = collection(this.db, NetworkConstants.COLLECTION_TRIDYS);
    const countQuery = query(ref, where('contextId', '==', contextId));
    const orderedPageQuery = lastDoc
      ? query(countQuery, orderBy('timeStamp', 'desc'), startAfter(lastDoc), limit(pageSize))
      : query(countQuery, orderBy('timeStamp', 'desc'), limit(pageSize));
    const fallbackPageQuery = lastDoc
      ? query(countQuery, startAfter(lastDoc), limit(pageSize))
      : query(countQuery, limit(pageSize));

    const countSnapshot = await getCountFromServer(countQuery);
    let snapshot;
    try {
      snapshot = await getDocs(orderedPageQuery);
    } catch (error) {
      console.warn('Falling back to unordered Tridy pagination. Add a Firestore index for contextId + timeStamp for stable ordering.', error);
      snapshot = await getDocs(fallbackPageQuery);
    }
    const items = snapshot.docs.map(doc => {
      const data = doc.data() as any;
      return { ...data, id: data?.id || doc.id };
    });
    const total = countSnapshot.data().count;

    return {
      items,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total,
      hasMore: snapshot.docs.length === pageSize
    };
  }

  observeTridysFirestore(){
    this.allTridys$ = []

    const userRolesPerContext$ = this.settingsService.userRolesPerContext$
    const contextId = this.settingsService.contextId$
    const publisherRoles = this.settingsService.publisherRoles
    const userRoles$ = this.settingsService.userRoles$
    const uid$ = this.settingsService.uid$
    const ref = collection(this.db, NetworkConstants.COLLECTION_TRIDYS);
    const q = query(ref, where('contextId', '==', contextId));
    
    return from(getDocs(q)).pipe(
      map(snapshot =>
        snapshot.docs.map( doc => {
          const data = doc.data() as any;
          return { ...data, id: data?.id || doc.id };
        })
      ),
      tap(items => {
        items.forEach( data => {
          if(data.contextId == contextId){
            if(  (userRolesPerContext$[contextId] && checkExistingRoles(userRolesPerContext$[contextId], publisherRoles) == 1 && userRolesPerContext$[contextId].includes('tracer'))
              || (userRoles$ && checkExistingRoles(userRoles$, publisherRoles) == 1 && userRoles$.includes('tracer'))  ){
              if(data['userId'] == uid$){
                // this.allTridys$.push(data)
                if(data.contentType){
                  if(data.contentType!='order' && data.contentType != 'item' && data.contentType!='Event'){
                    this.observeTridyContentTypeName(contextId, data.contentType).subscribe( value => {
                      if(value)
                        data['contentName'] = value
                    })
                  }
                }
              }
            }else{
              // this.allTridys$.push(data)
              if(data.contentType){
                if(data.contentType!='order' && data.contentType != 'item' && data.contentType!='Event'){
                  this.observeTridyContentTypeName(contextId, data.contentType).subscribe( value => {
                    if(value)
                      data['contentName'] = value
                  })
                }
              }
            }
          }
        })
        this.allTridys$ = items;
      })
    );
  }

  observeTridyContentTypeName(contextId, contentTypeId){    
    const ref = doc(this.db, `${NetworkConstants.COLLECTION_CONTEXTS}/${contextId}/${NetworkConstants.COLLECTION_CONTENT_TYPES}/${contentTypeId}`);
    return from(getDoc(ref)).pipe(
      map(snapshot => {
        if (!snapshot.exists()) return null;

        const data = snapshot.data() as any;
        if (data?.displayName)
          return data.displayName;

        return null;
      })
    );
  }

  private valueAtPath(source: Record<string, unknown> | undefined, path: string): unknown {
    return path.split('.').reduce<unknown>((value, key) =>
      value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined,
    source);
  }

  private identifierKey(value: unknown): string {
    if (value instanceof Date) return value.toISOString();
    return String(value).trim();
  }
  
}
